import React, { useEffect, useState } from "react";
import { Plus, Trash2, GripVertical, Save, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { resolveImageUrl } from "@/lib/site";
import ImageField from "@/pages/admin/ImageField";
import { useSite } from "@/context/SiteContext";

function TeaserCard() {
  const { site, save, reload } = useSite();
  const t = (site && site.gallery_teaser) || {};
  const [form, setForm] = useState(t);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm((site && site.gallery_teaser) || {});
    // eslint-disable-next-line
  }, [site?.gallery_teaser]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await save("gallery_teaser", form);
      await reload();
      toast.success("Teaser sparad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[var(--border)] rounded-2xl" data-testid="gallery-teaser-form">
      <div className="p-6 border-b border-[var(--border)]">
        <h3 className="font-serif-display text-2xl font-bold">Galleriknapp</h3>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Rutan på hemsidan som öppnar galleriet. Ändra rubrik, text och omslagsbild.
        </p>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <ImageField
            label="Omslagsbild"
            value={form.cover_url || ""}
            onChange={(v) => update("cover_url", v)}
            testId="gallery-teaser-cover"
          />
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Överrubrik</span>
          <input
            type="text"
            value={form.eyebrow ?? ""}
            onChange={(e) => update("eyebrow", e.target.value)}
            className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Knappens text</span>
          <input
            type="text"
            value={form.cta_text ?? ""}
            onChange={(e) => update("cta_text", e.target.value)}
            className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Rubrik</span>
          <input
            type="text"
            value={form.title ?? ""}
            onChange={(e) => update("title", e.target.value)}
            className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Underrubrik</span>
          <input
            type="text"
            value={form.subtitle ?? ""}
            onChange={(e) => update("subtitle", e.target.value)}
            className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
          />
        </label>
      </div>
      <div className="flex justify-end p-4 bg-[var(--surface-alt)] border-t border-[var(--border)] rounded-b-2xl">
        <button
          type="submit"
          disabled={saving}
          data-testid="gallery-teaser-save"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white disabled:opacity-50"
        >
          <Save size={15} /> {saving ? "Sparar…" : "Spara teaser"}
        </button>
      </div>
    </form>
  );
}

export default function GalleryEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ url: "", caption: "" });
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const { data } = await api.get("/gallery");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const addImage = async () => {
    if (!draft.url) {
      toast.error("Ladda upp eller klistra in en bild först");
      return;
    }
    setAdding(true);
    try {
      const nextOrder = items.length ? Math.max(...items.map((i) => i.sort_order || 0)) + 10 : 10;
      await api.post("/gallery", { url: draft.url, caption: draft.caption, sort_order: nextOrder });
      setDraft({ url: "", caption: "" });
      toast.success("Bild tillagd");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte lägga till");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Ta bort bilden från galleriet?")) return;
    try {
      await api.delete(`/gallery/${it.id}`);
      await load();
      toast.success("Borttagen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  const updateCaption = async (it, caption) => {
    try {
      await api.put(`/gallery/${it.id}`, { caption });
      toast.success("Bildtext sparad");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[i];
    const b = items[j];
    try {
      await Promise.all([
        api.put(`/gallery/${a.id}`, { sort_order: b.sort_order }),
        api.put(`/gallery/${b.id}`, { sort_order: a.sort_order }),
      ]);
      await load();
    } catch (err) {
      toast.error("Kunde inte ändra ordning");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-black">Galleri</h1>
        <p className="text-[var(--text-muted)] mt-1">
          Bilder som visas i galleriet på hemsidan. Dra, redigera bildtexter, ta bort eller lägg till nya.
        </p>
      </div>

      <TeaserCard />

      {/* Add new */}
      <div className="bg-white border border-[var(--border)] rounded-2xl p-6 space-y-4" data-testid="gallery-add-form">
        <h3 className="font-serif-display text-xl font-bold">Lägg till bild</h3>
        <ImageField
          label="Bild"
          value={draft.url}
          onChange={(v) => setDraft((d) => ({ ...d, url: v }))}
          testId="gallery-add-image"
        />
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Bildtext (valfri)</span>
          <input
            type="text"
            value={draft.caption}
            onChange={(e) => setDraft({ ...draft, caption: e.target.value })}
            placeholder="t.ex. Dagens lunchbuffé"
            className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            data-testid="gallery-add-caption"
          />
        </label>
        <div className="flex justify-end">
          <button
            onClick={addImage}
            disabled={adding || !draft.url}
            data-testid="gallery-add-submit"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white disabled:opacity-50"
          >
            <Plus size={15} /> {adding ? "Lägger till…" : "Lägg till i galleri"}
          </button>
        </div>
      </div>

      {/* Existing images */}
      <div className="bg-white border border-[var(--border)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-xl font-bold">Alla bilder</h3>
          <span className="text-sm text-[var(--text-muted)]">{items.length} st</span>
        </div>
        {loading ? (
          <div className="py-8 text-center text-[var(--text-muted)]">Laddar…</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-[var(--text-muted)]">
            Inga bilder än. Lägg till din första bild ovan.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it, i) => (
              <div
                key={it.id}
                data-testid={`gallery-item-${it.id}`}
                className="rounded-2xl overflow-hidden border border-[var(--border)] bg-[var(--surface-alt)]"
              >
                <div className="aspect-square overflow-hidden">
                  <img src={resolveImageUrl(it.url)} alt={it.caption || ""} className="w-full h-full object-cover" />
                </div>
                <div className="p-3 space-y-2">
                  <input
                    type="text"
                    defaultValue={it.caption || ""}
                    onBlur={(e) => {
                      if (e.target.value !== (it.caption || "")) updateCaption(it, e.target.value);
                    }}
                    placeholder="Bildtext"
                    className="w-full bg-white border border-[var(--border)] rounded-lg px-3 py-2 text-sm"
                    data-testid={`gallery-caption-${it.id}`}
                  />
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex gap-1">
                      <button
                        onClick={() => move(i, -1)}
                        disabled={i === 0}
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white disabled:opacity-30"
                        aria-label="Flytta upp"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        onClick={() => move(i, 1)}
                        disabled={i === items.length - 1}
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white disabled:opacity-30"
                        aria-label="Flytta ner"
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>
                    <button
                      onClick={() => remove(it)}
                      data-testid={`gallery-delete-${it.id}`}
                      className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-white"
                    >
                      <Trash2 size={13} /> Ta bort
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
